import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import VerifiedIcon from '@mui/icons-material/Verified';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import Container from '@/components/PageSheet';
import FlowSummary from '@/components/FlowSummary';
import DiscountField from '@/components/DiscountField';
import ConsultationProgress from '@/components/ConsultationProgress';
import { localized } from '@/utils/localizedContent';
import { consultationsApi } from '@/services/api';

/** The AI/confirmed report body, rendered in a sandboxed iframe. */
function ReportFrame({ html, title }) {
  const [expanded, setExpanded] = useState(false);
  if (!html) return null;
  return (
    <Box>
      <iframe
        title={title}
        srcDoc={html}
        sandbox="allow-same-origin"
        style={{
          border: 'none',
          width: '100%',
          height: expanded ? '75vh' : 300,
          borderRadius: 12,
          background: '#fff',
        }}
      />
      <Button size="small" startIcon={<OpenInFullIcon />} onClick={() => setExpanded((e) => !e)}>
        {expanded ? title : title}
      </Button>
    </Box>
  );
}

/** Chat thread with the physician. */
function ChatBox({ consultationId }) {
  const { t, i18n } = useTranslation();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [draft, setDraft] = useState('');
  const endRef = useRef(null);
  const dateLabel = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString(i18n.language === 'fa' ? 'fa-IR' : 'en-GB', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '';

  const { data: thread, isLoading } = useQuery({
    queryKey: ['consultation-messages', consultationId],
    queryFn: () => consultationsApi.listMessages(consultationId),
    // Only poll while the conversation can actually change. A closed or
    // not-yet-open window cannot receive messages, so polling it every 20s for
    // the life of the page is pure cost — and a confirmed case sits in that
    // state far longer than it was ever open.
    refetchInterval: (q) => (q.state.data?.chatState === 'open' ? 20000 : false),
  });
  const messages = thread?.messages;
  // RULE 6 — the server owns the window; we only render what it reports.
  const chatState = thread?.chatState || 'not_open';
  const chatExpiresAt = thread?.chatExpiresAt;
  const canWrite = chatState === 'open';

  const [sendError, setSendError] = useState('');
  const sendMut = useMutation({
    mutationFn: (body) => consultationsApi.sendMessage(consultationId, body),
    onSuccess: () => {
      setDraft('');
      setSendError('');
      qc.invalidateQueries({ queryKey: ['consultation-messages', consultationId] });
      qc.invalidateQueries({ queryKey: ['my-consultations'] });
    },
    onError: (err) => {
      const code = err?.response?.data?.error?.code;
      if (code === 'CHAT_WINDOW_CLOSED') setSendError(t('consultations.chat.closedError'));
      else if (code === 'CHAT_NOT_OPEN') setSendError(t('consultations.chat.notOpenError'));
      else setSendError(t('common.error'));
      qc.invalidateQueries({ queryKey: ['consultation-messages', consultationId] });
    },
  });

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'nearest' });
  }, [messages]);

  const time = (iso) =>
    iso
      ? new Date(iso).toLocaleTimeString(i18n.language === 'fa' ? 'fa-IR' : 'en-US', {
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';

  const send = () => {
    const body = draft.trim();
    if (body && canWrite && !sendMut.isPending) sendMut.mutate(body);
  };

  return (
    <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
      <CardContent>
        <Typography fontWeight={800} sx={{ mb: 1 }}>
          {t('consultations.chat.title')}
        </Typography>

        {/* RULE 6 — the client should always know how long they have. */}
        {chatState === 'not_open' && (
          <Alert severity="info" sx={{ mb: 1.5, borderRadius: 2 }}>
            {t('consultations.chat.notOpen')}
          </Alert>
        )}
        {chatState === 'open' && chatExpiresAt && (
          <Alert severity="success" variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
            {t('consultations.chat.openUntil', { date: dateLabel(chatExpiresAt) })}
          </Alert>
        )}
        {chatState === 'closed' && (
          <Alert severity="warning" variant="outlined" sx={{ mb: 1.5, borderRadius: 2 }}>
            <AlertTitle sx={{ fontWeight: 800 }}>{t('consultations.chat.closedTitle')}</AlertTitle>
            {t('consultations.chat.closedBody')}
          </Alert>
        )}
        <Box sx={{ maxHeight: 320, overflowY: 'auto', mb: 1.5 }}>
          {isLoading ? (
            <Skeleton variant="rounded" height={80} />
          ) : (messages || []).length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('consultations.chat.empty')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {messages.map((m) => {
                const mine = m.author === 'client';
                return (
                  <Box
                    key={m.id}
                    sx={{ display: 'flex', justifyContent: mine ? 'flex-end' : 'flex-start' }}
                  >
                    <Box
                      sx={{
                        maxWidth: '80%',
                        px: 1.5,
                        py: 1,
                        borderRadius: 2.5,
                        bgcolor: mine ? 'primary.main' : 'action.hover',
                        color: mine ? 'primary.contrastText' : 'text.primary',
                      }}
                    >
                      {!mine && m.authorName && (
                        <Typography variant="caption" fontWeight={800} sx={{ display: 'block' }}>
                          {m.authorName}
                        </Typography>
                      )}
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {m.body}
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7 }}>
                        {time(m.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
              <div ref={endRef} />
            </Stack>
          )}
        </Box>
        {canWrite ? (
          <>
            <Stack direction="row" spacing={1}>
              <TextField
                fullWidth
                size="small"
                multiline
                maxRows={4}
                placeholder={t('consultations.chat.placeholder')}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    send();
                  }
                }}
              />
              <IconButton
                color="primary"
                onClick={send}
                disabled={!draft.trim() || sendMut.isPending}
                aria-label={t('consultations.chat.send')}
              >
                <SendIcon />
              </IconButton>
            </Stack>
            {sendError && (
              <Alert severity="error" sx={{ mt: 1, borderRadius: 2 }}>
                {sendError}
              </Alert>
            )}
          </>
        ) : (
          // Reading stays open forever; only writing is gated. If they still
          // need help, point them somewhere that can actually answer.
          <Button fullWidth variant="outlined" onClick={() => navigate('/support')}>
            {t('consultations.chat.contactSupport')}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function ConsultationDetailPage() {
  const { consultationId } = useParams();
  // Was missing: the two recovery buttons below (choose a doctor / choose an
  // analysis) called navigate(), which only existed inside ChatBox. Clicking
  // them threw a ReferenceError inside the handler, so the page simply did
  // nothing — the exact symptom after support sends a case back a step.
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  // The unreviewed draft is opt-in. Default to progress; only fetch the draft
  // once the client has explicitly asked to see it.
  const [showDraft, setShowDraft] = useState(false);
  const [payError, setPayError] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [gatewayOpen, setGatewayOpen] = useState(false);
  // The code the client entered and the server validated. Only the code is
  // held — the price is the server's to compute at /pay.
  const [discountCode, setDiscountCode] = useState(null);

  // Which gateways the server says it has. This list decides ONE thing: whether
  // the buyer is asked to choose. It is not a precondition for paying — the
  // server resolves the gateway itself when this app names none, so a terms
  // call that is slow, stale, failed, or absent from an older bundle can no
  // longer stand between someone and the bank.
  const termsQuery = useQuery({
    queryKey: ['consultation-terms'],
    queryFn: () => consultationsApi.getConsultationTerms(),
    staleTime: 5 * 60 * 1000,
  });
  const gateways = termsQuery.data?.gateways ?? [];
  // The server also says whether it can settle without a gateway (the dev
  // bypass). With neither, nothing on that server can take a payment — worth
  // saying out loud, but only as information: the button stays live, because
  // this is a report that can be wrong and a sale must not hinge on it.
  const payable = gateways.length > 0 || Boolean(termsQuery.data?.paymentBypass);
  const paymentUnavailable = termsQuery.isSuccess && !payable;
  // the "continue" arrow must point the way the language reads
  const Forward = i18n.dir() === 'rtl' ? ArrowBackIcon : ArrowForwardIcon;

  // Payment is bound to THIS consultation, so the capture and the doctor the
  // client already chose carry through instead of starting a second case.
  // The pay step needs the consultation itself (service, clinic, physician);
  // this component only ever fetched the report, so those fields were
  // undefined the moment a capture-first case reached 'awaiting_purchase'.
  const qc = useQueryClient();

  const { data: consultation } = useQuery({
    queryKey: ['consultation', consultationId],
    queryFn: () => consultationsApi.getConsultation(consultationId),
    // The progress tracker reads this. Without a poll it only advanced when the
    // page was remounted, so a report could land and the client would still see
    // "analysis running" until they navigated away and back.
    // Stop once the case is finished — there is nothing left to watch.
    refetchInterval: (q) =>
      (['confirmed', 'needs_in_person', 'expired'].includes(q.state.data?.status)
        ? false : 30000),
    refetchOnWindowFocus: true,
  });

  const payMut = useMutation({
    mutationFn: (gateway) =>
      consultationsApi.payConsultation(consultationId, gateway, discountCode),
    onSuccess: (res) => {
      // The gateway is not wired yet, so this settles server-side and comes
      // straight back. When it is, the same call returns a paymentUrl.
      if (res?.paymentUrl) { window.location.assign(res.paymentUrl); return; }
      qc.invalidateQueries({ queryKey: ['consultation', consultationId] });
      qc.invalidateQueries({ queryKey: ['consultation-report', consultationId] });
      qc.invalidateQueries({ queryKey: ['my-consultations'] });
    },
    onError: (err) =>
      setPayError(err?.response?.data?.error?.message || t('common.error')),
  });

  const { data: report, isLoading } = useQuery({
    queryKey: ['consultation-report', consultationId, showDraft],
    queryFn: () => consultationsApi.getConsultationReport(consultationId, { draft: showDraft }),
    // While the physician hasn't confirmed, keep checking for the final version.
    refetchInterval: (q) => (q.state.data?.reportStatus === 'confirmed' ? false : 30000),
  });

  const fmt = (iso) =>
    iso
      ? new Date(iso).toLocaleDateString(lang === 'fa' ? 'fa-IR' : 'en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '';
  const status = report?.reportStatus;

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 2, sm: 4 } }}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
        {t('consultations.detailTitle')}
      </Typography>

      {isLoading ? (
        <Skeleton variant="rounded" height={160} sx={{ mb: 2 }} />
      ) : (
        <Stack spacing={2} sx={{ mb: 2 }}>
          {status === 'processing' && (
            <Card
              elevation={0}
              sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
            >
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <HourglassEmptyIcon sx={{ fontSize: 44, color: 'primary.main', mb: 1 }} />
                <Typography fontWeight={800}>{t('consultations.processingTitle')}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('consultations.processingMessage')}
                </Typography>
              </CardContent>
            </Card>
          )}

          {status === 'failed' && (
            <Alert severity="error" variant="outlined" sx={{ borderRadius: 3 }}>
              <AlertTitle sx={{ fontWeight: 800 }}>{t('consultations.failed.title')}</AlertTitle>
              {t('consultations.failed.body')}
            </Alert>
          )}

          <ConsultationProgress consultation={consultation} report={report} t={t} />

          {/* Which gateway. Asked at the moment of paying rather than stored as
              a preference: people choose by which card they have to hand, and
              that changes between purchases. */}
          <Dialog open={gatewayOpen} onClose={() => setGatewayOpen(false)} fullWidth maxWidth="xs">
            <DialogTitle sx={{ fontWeight: 800 }}>{t('pay.chooseTitle')}</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {t('pay.chooseBody')}
              </Typography>
              {gateways.length === 0 && (
                <Alert severity="warning" sx={{ mb: 2 }}>{t('pay.noneAvailable')}</Alert>
              )}
              {/* The code was entered on the card, before this dialog opened —
                  pressing a gateway button sends the client to the bank, so
                  there is no screen left to type it on. Restated here so the
                  amount about to be charged is visible at the last moment. */}
              {discountCode ? (
                <Alert severity="success" sx={{ mb: 2 }}>
                  {t('discount.appliedCode', { code: discountCode })}
                </Alert>
              ) : null}
              <Stack spacing={1.25}>
                {gateways.map((g) => (
                  <Button
                    key={g}
                    fullWidth
                    size="large"
                    variant="outlined"
                    disabled={payMut.isPending}
                    onClick={() => { setGatewayOpen(false); payMut.mutate(g); }}
                    sx={{ justifyContent: 'space-between', py: 1.4, borderRadius: 3 }}
                    endIcon={<Forward />}
                  >
                    <Box sx={{ textAlign: 'start' }}>
                      <Typography fontWeight={800}>{t(`pay.gateway.${g}`)}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {t(`pay.gatewayHint.${g}`)}
                      </Typography>
                    </Box>
                  </Button>
                ))}
              </Stack>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setGatewayOpen(false)}>{t('common.cancel')}</Button>
            </DialogActions>
          </Dialog>

          {/* Polling covers the passive case; this is for someone actively
              waiting who does not want to count to thirty. */}
          {!['confirmed', 'needs_in_person'].includes(consultation?.status) && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: -1, mb: 2 }}>
              <Button
                size="small"
                startIcon={<RefreshIcon />}
                disabled={isRefreshing}
                onClick={async () => {
                  setIsRefreshing(true);
                  try {
                    await Promise.all([
                      qc.refetchQueries({ queryKey: ['consultation', consultationId] }),
                      qc.refetchQueries({ queryKey: ['consultation-report', consultationId] }),
                    ]);
                  } finally {
                    setIsRefreshing(false);
                  }
                }}
              >
                {isRefreshing ? t('progress.checking') : t('progress.recheck')}
              </Button>
            </Box>
          )}

          {status === 'awaiting_purchase' && (
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <HourglassEmptyIcon sx={{ fontSize: 44, color: 'secondary.main', mb: 1 }} />
                <Typography fontWeight={800}>{t('consultations.awaitingPurchase.title')}</Typography>
                <FlowSummary consultation={consultation} lang={lang} t={t}
                  sx={{ mt: 2, mb: 0, textAlign: 'start' }} />
                <Typography variant="body2" color="text.secondary" sx={{ mt: .5, mb: 2 }}>
                  {report.hasCapture
                    ? t('consultations.awaitingPurchase.captured')
                    : t('consultations.awaitingPurchase.needsCapture')}
                </Typography>

                {/* The last step of the free stage: everything above is done,
                    this is where money is asked for — and only here. */}
                {!consultation?.physicianName ? (
                  <Button variant="contained" size="large"
                    onClick={() => navigate(`/consultations/${encodeURIComponent(consultationId)}/choose-physician`)}>
                    {t('consultations.awaitingPurchase.choosePhysician')}
                  </Button>
                ) : !consultation?.serviceId ? (
                  <Button variant="contained" size="large"
                    onClick={() => navigate(`/consultations/${encodeURIComponent(consultationId)}/choose-service`)}>
                    {t('consultations.awaitingPurchase.chooseService')}
                  </Button>
                ) : (
                  <Stack spacing={1} alignItems="center">
                    {consultation?.priceToman ? (
                      <Box sx={{ width: '100%', maxWidth: 360 }}>
                        <Typography variant="body2" color="text.secondary" align="center">
                          {t('card.priceToman', {
                            amount: new Intl.NumberFormat(lang === 'fa' ? 'fa-IR' : 'en-GB')
                              .format(consultation.priceToman),
                          })}
                        </Typography>
                        <DiscountField
                          amountToman={consultation.priceToman}
                          context="consultation"
                          clinicCode={consultation?.clinicCode}
                          onChange={setDiscountCode}
                          t={t}
                          lang={lang}
                        />
                      </Box>
                    ) : null}
                    {/* Said before the button is pressed, not after a failed
                        request: there is nothing the client can do about an
                        unconfigured gateway, and an error under a button they
                        just pressed reads as something they did wrong. It does
                        NOT disable the button — this is what the server said a
                        moment ago, not a fact about the buyer's money, and a
                        wrong answer here must never be what stops a sale. */}
                    {paymentUnavailable && (
                      <Alert severity="warning" sx={{ width: '100%', maxWidth: 360 }}>
                        {t('pay.noneAvailable')}
                      </Alert>
                    )}
                    <Button variant="contained" size="large"
                      disabled={payMut.isPending}
                      onClick={() => {
                        // Ask only when there is something to ask about. One
                        // gateway is not a choice, and an empty list is not a
                        // verdict — it may only mean this app has not managed
                        // to ask yet. In both cases the server resolves which
                        // gateway to use, so send the request and let it.
                        if (gateways.length > 1) { setGatewayOpen(true); return; }
                        payMut.mutate(gateways[0]);
                      }}>
                      {payMut.isPending ? t('common.loading') : t('consultations.awaitingPurchase.pay')}
                    </Button>
                    <Typography variant="caption" color="text.secondary">
                      {t('consultations.awaitingPurchase.payHint', {
                        name: consultation.physicianName,
                      })}
                    </Typography>
                    {payError && <Alert severity="error" sx={{ mt: 1 }}>{payError}</Alert>}
                  </Stack>
                )}
              </CardContent>
            </Card>
          )}

          {/* Analysis is done, the doctor is reading it. The draft exists but
              is deliberately not shown: an unreviewed symmetry figure under a
              "please wait" banner is what alarms people. Offer it as a choice. */}
          {status === 'reviewing' && (
            <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}>
              <CardContent sx={{ py: 4 }}>
                <Stack spacing={1.5} alignItems="center" textAlign="center">
                  <VerifiedIcon sx={{ fontSize: 42, color: 'secondary.main' }} />
                  <Typography fontWeight={800}>{t('consultations.reviewing.title')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {report.physicianName
                      ? t('consultations.reviewing.body', {
                          name: report.physicianName, hours: report.slaHours ?? 48 })
                      : t('consultations.reviewing.bodyNoName', { hours: report.slaHours ?? 48 })}
                  </Typography>
                  {report.draftAvailable && (
                    <Button size="small" variant="text" sx={{ mt: 1 }}
                      onClick={() => setShowDraft(true)}>
                      {t('consultations.reviewing.showDraft')}
                    </Button>
                  )}
                  <Typography variant="caption" color="text.disabled">
                    {t('consultations.reviewing.showDraftHint')}
                  </Typography>
                </Stack>
              </CardContent>
            </Card>
          )}

          {status === 'preliminary' && (
            <>
              {/* Shown only because the client asked for it. */}
              <Alert severity="warning" variant="outlined" sx={{ borderRadius: 3 }}>
                <AlertTitle sx={{ fontWeight: 800 }}>
                  {t('consultations.preliminary.title')}
                </AlertTitle>
                {t('consultations.preliminary.body', {
                  name: report.physicianName || t('consultations.preliminary.yourDoctor'),
                })}
                {report.physicianName && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {t('consultations.preliminary.waitingFor', { name: report.physicianName })}
                  </Typography>
                )}
                <Button size="small" variant="text" sx={{ mt: 1, px: 0 }}
                  onClick={() => setShowDraft(false)}>
                  {t('consultations.reviewing.hideDraft')}
                </Button>
              </Alert>
              <Card
                elevation={0}
                sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
              >
                <CardContent>
                  <Chip
                    size="small"
                    color="warning"
                    variant="outlined"
                    label={t('consultations.preliminary.tag')}
                    sx={{ mb: 1 }}
                  />
                  {/* `summary` is bilingual JSON ({fa,en}) from the analysis
                      row, not a string — rendering it raw throws "Objects are
                      not valid as a React child" and takes the page down. */}
                  {localized(report.summary, lang) && (
                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                      {localized(report.summary, lang)}
                    </Typography>
                  )}
                  <ReportFrame html={report.html} title={t('consultations.toggleReport')} />
                </CardContent>
              </Card>
            </>
          )}

          {status === 'confirmed' && (
            <>
              {report.wasSuperseded && (
                <Alert severity="info" sx={{ borderRadius: 3 }}>
                  {t('consultations.superseded.notice')}
                </Alert>
              )}
              <Alert
                severity="success"
                variant="outlined"
                sx={{ borderRadius: 3 }}
                icon={<VerifiedIcon />}
              >
                <AlertTitle sx={{ fontWeight: 800 }}>
                  {t('consultations.confirmed.title')}
                </AlertTitle>
                {t('consultations.confirmed.body', { name: report.physicianName || '' })}
                {report.confirmedAt && (
                  <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                    {fmt(report.confirmedAt)}
                  </Typography>
                )}
              </Alert>

              {report.physicianNote && (
                <Card
                  elevation={0}
                  sx={{ borderRadius: 3, border: '1px solid', borderColor: 'success.main' }}
                >
                  <CardContent>
                    <Typography
                      variant="caption"
                      fontWeight={800}
                      color="success.main"
                      sx={{ display: 'block', mb: 0.5 }}
                    >
                      {t('consultations.confirmed.guidance')}
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {report.physicianNote}
                    </Typography>
                    {report.disposition === 'needs_in_person' && (
                      <Chip
                        size="small"
                        color="secondary"
                        sx={{ mt: 1 }}
                        label={t('consultations.confirmed.needsInPerson')}
                      />
                    )}
                  </CardContent>
                </Card>
              )}

              <Card
                elevation={0}
                sx={{ borderRadius: 3, border: '1px solid', borderColor: 'divider' }}
              >
                <CardContent>
                  <Chip
                    size="small"
                    color="success"
                    icon={<VerifiedIcon />}
                    label={t('consultations.confirmed.tag')}
                    sx={{ mb: 1 }}
                  />
                  {/* `summary` is bilingual JSON ({fa,en}) from the analysis
                      row, not a string — rendering it raw throws "Objects are
                      not valid as a React child" and takes the page down. */}
                  {localized(report.summary, lang) && (
                    <Typography variant="body2" sx={{ mb: 1, whiteSpace: 'pre-wrap' }}>
                      {localized(report.summary, lang)}
                    </Typography>
                  )}
                  <ReportFrame html={report.html} title={t('consultations.toggleReport')} />
                </CardContent>
              </Card>
            </>
          )}
        </Stack>
      )}

      <Divider sx={{ my: 2 }} />
      <ChatBox consultationId={consultationId} />
    </Container>
  );
}
